import { useEffect, useState } from 'react';
import { createStore, type StateCreator, useStore } from 'zustand';

/**
 * Creates a hook that provides a scoped Zustand store instance.
 *
 * Behavior:
 * - A new store instance is created per mounted component that calls the returned hook.
 * - Different component instances do not share the same scoped store state.
 * - If `onSubscribe` writes to shared/module-level state outside the scoped store,
 *   multiple mounted components can overwrite each other (last write wins).
 *
 * Intended usage:
 * - Safe default for component-local store state.
 * - If `onSubscribe` synchronizes into a global singleton, use only one active
 *   producer per page/tab (or implement your own arbitration strategy).
 */
export const createScopedStoreHook = <TState>(
	createState: StateCreator<TState>,
	options?: {
		onSubscribe?: (state: TState, prevState: TState) => void;
		onUnmount?: (state: TState) => void;
	}
) => {
	const createStoreInstance = () => createStore<TState>()(createState);

	return function useScopedStore(): TState {
		const [store] = useState(createStoreInstance);

		useEffect(() => {
			const unsubscribe = options?.onSubscribe
				? store.subscribe(options.onSubscribe)
				: undefined;

			return () => {
				options?.onUnmount?.(store.getState());
				unsubscribe?.();
			};
		}, [store]);

		return useStore(store);
	};
};
